import type { SocialPlatformData, SocialConnection } from '@/mocks/superAdminSocialStatus';

interface Props {
  platform: SocialPlatformData;
  connection: SocialConnection;
  onClose: () => void;
}

export default function HistoryModal({ platform, connection, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden border border-background-200 flex flex-col">
        {/* Header */}
        <div className="shrink-0 bg-background-50 rounded-t-2xl border-b border-background-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-foreground-900 font-bold text-sm">Historial de Conexión</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <div
                className="w-5 h-5 rounded-md flex items-center justify-center text-white"
                style={{ backgroundColor: platform.color }}
              >
                <i className={`${platform.icon} text-[10px]`}></i>
              </div>
              <p className="text-foreground-500 text-[11px]">
                {connection.radioName} · {platform.label}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {connection.history.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-history-line text-foreground-400 text-lg"></i>
              </div>
              <p className="text-foreground-500 text-xs font-medium">Sin eventos registrados</p>
              <p className="text-foreground-400 text-[10px] mt-0.5">El historial aparecerá aquí cuando haya actividad</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-background-200"></div>

              <div className="space-y-4">
                {connection.history.map((event, idx) => {
                  const isLast = idx === connection.history.length - 1;
                  const isError = event.result.toLowerCase().includes('fall') || event.result.toLowerCase().includes('error');
                  const isSuccess = event.result.toLowerCase().includes('exitosa') || event.result.toLowerCase().includes('aprobada');
                  const dotColor = isError ? 'bg-red-400' : isSuccess ? 'bg-emerald-400' : 'bg-amber-400';
                  const dotRing = isError ? 'ring-red-100' : isSuccess ? 'ring-emerald-100' : 'ring-amber-100';

                  return (
                    <div key={idx} className="flex gap-4">
                      {/* Dot */}
                      <div className="relative z-10 shrink-0 mt-0.5">
                        <div className={`w-[10px] h-[10px] rounded-full ${dotColor} ring-4 ${dotRing}`}></div>
                      </div>

                      {/* Content */}
                      <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-1'}`}>
                        <div className="bg-background-100 rounded-xl p-3">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="text-foreground-800 text-xs font-semibold">{event.action}</p>
                            <span className="text-foreground-400 text-[9px] shrink-0 whitespace-nowrap">
                              {new Date(event.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className={`text-[11px] font-medium ${
                            isError ? 'text-red-600' : isSuccess ? 'text-emerald-600' : 'text-amber-600'
                          }`}>
                            {event.result}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-background-200 px-6 py-3 flex items-center justify-between">
          <p className="text-foreground-400 text-[10px]">
            {connection.history.length} evento{connection.history.length !== 1 ? 's' : ''}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}